import React, { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Sparkle, X, FloppyDisk, Eye, MagicWand, BookmarkSimple, ChartLineUp, CaretDown, CaretRight } from '@phosphor-icons/react';
import { toast } from 'sonner';

/**
 * Per-campaign Product + 6-step sequence editor.
 *
 * Lets the user attach a specific product (e.g. "Betonverf voor outdoor pools")
 * to a sequence campaign, AI-generate 6 step-configs tailored to that product +
 * target audience, edit them inline, and preview how many facilities/contacts
 * the segment_filter currently matches.
 */
export default function CampaignProductModal({ open, onClose, campaign, apiBase, onSaved }) {
  const [product, setProduct] = useState('');
  const [productDescription, setProductDescription] = useState('');
  const [targetDescription, setTargetDescription] = useState('');
  const [steps, setSteps] = useState([]);
  const [segmentFilter, setSegmentFilter] = useState({});
  const [filterConfig, setFilterConfig] = useState(null);
  const [preview, setPreview] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  // Email preview state (per step)
  const [emailPreview, setEmailPreview] = useState(null); // {step, subject, html_body, sample_contact, sample_facility}
  const [previewingStep, setPreviewingStep] = useState(null);
  // Sequence Coach state
  const [coachResult, setCoachResult] = useState(null); // {overall_score, overall_assessment, steps:[...]}
  const [coaching, setCoaching] = useState(false);
  const [expandedCoach, setExpandedCoach] = useState({}); // {[stepNum]: bool}
  const [coachHistory, setCoachHistory] = useState([]); // chronological runs
  const [appliedPatterns, setAppliedPatterns] = useState([]); // insights used in last generate
  const [autoCoach, setAutoCoach] = useState(null); // {initial_score, final_score, self_healed, weak_step}
  const [prediction, setPrediction] = useState(null); // {predicted_reply_rate, low_pct, high_pct, sample_size, ...}
  const [predicting, setPredicting] = useState(false);
  // Variant Battle (Auto-Improve)
  const [variantBattle, setVariantBattle] = useState(null); // {variants:[...], winner_id, pool_size}
  const [autoImproving, setAutoImproving] = useState(false);
  const [expandedVariant, setExpandedVariant] = useState(null); // variant_id
  const [adoptingVariant, setAdoptingVariant] = useState(null); // variant_id while POSTing
  // Variant Battle Retrospective (actual vs predicted reply-rate)
  const [retro, setRetro] = useState(null); // {eligible, sent, actual_reply_rate, adopted_error, variants, ...}
  const [retroLoading, setRetroLoading] = useState(false);
  // Per-step performance + A/B variants
  const [perStep, setPerStep] = useState(null); // {steps:[{step,sent,reply_rate,predicted,delta,verdict,...}], per_step_variants:[...]}
  const [perStepLoading, setPerStepLoading] = useState(false);
  const [rewritingStep, setRewritingStep] = useState(null); // step number while rewrite in flight
  const [enablingAb, setEnablingAb] = useState(false);
  const [adoptingWinners, setAdoptingWinners] = useState(false);

  useEffect(() => {
    if (!open || !campaign) return;
    setProduct(campaign.product || '');
    setProductDescription(campaign.product_description || '');
    setTargetDescription(campaign.target_description || '');
    setSteps(campaign.sequence_steps || []);
    setSegmentFilter(campaign.segment_filter || {});
    setPreview(null);
    setCoachResult(campaign.coach_last_run || null);
    setExpandedCoach({});
    setCoachHistory([]);
    setAppliedPatterns(campaign.applied_insight_patterns || []);
    setAutoCoach(null);
    setPrediction(null);
    setVariantBattle(campaign.variant_battle || null);
    setExpandedVariant(null);
    setRetro(null);
    setPerStep(null);
    // Load history if a campaign-id exists
    if (campaign.campaign_id) {
      fetch(`${apiBase}/email/campaigns/${campaign.campaign_id}/coach-history`, { credentials: 'include' })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => { if (d && d.runs) setCoachHistory(d.runs); })
        .catch(() => {});
      // Retrospective is only meaningful when a battle ran AND was adopted.
      if (campaign.variant_battle && campaign.adopted_variant) {
        setRetroLoading(true);
        fetch(`${apiBase}/email/campaigns/${campaign.campaign_id}/variant-retrospective`, { credentials: 'include' })
          .then((r) => (r.ok ? r.json() : null))
          .then((d) => { if (d) setRetro(d); })
          .catch(() => {})
          .finally(() => setRetroLoading(false));
      }
      // Per-step performance always loads — shows 'no_data' for un-sent campaigns
      setPerStepLoading(true);
      fetch(`${apiBase}/email/campaigns/${campaign.campaign_id}/per-step-metrics`, { credentials: 'include' })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => { if (d) setPerStep(d); })
        .catch(() => {})
        .finally(() => setPerStepLoading(false));
    }
    // Load profile filter config (dynamic dimensions per profile)
    fetch(`${apiBase}/profiles/filter-config`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setFilterConfig(d))
      .catch(() => {});
  }, [open, campaign, apiBase]);

  // Auto-debounce: predict reply-rate whenever the focus-texts or product change.
  // Lives BEFORE any early return so the hook order is stable across renders.
  useEffect(() => {
    if (!open || !campaign) return;
    if (!steps || steps.length === 0) {
      setPrediction(null);
      return;
    }
    const t = setTimeout(() => { reloadPrediction(steps); }, 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(steps.map(s => s.focus)), product, open]);

  if (!open || !campaign) return null;

  const generate = async () => {
    if (!product.trim()) {
      toast.error('Product naam is verplicht');
      return;
    }
    setGenerating(true);
    try {
      const r = await fetch(`${apiBase}/email/campaigns/${campaign.campaign_id}/generate-sequence`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product,
          product_description: productDescription,
          target_description: targetDescription,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Generatie mislukt');
      setSteps(d.sequence_steps || []);
      setAppliedPatterns(d.applied_patterns || []);
      setAutoCoach(d.auto_coach || null);
      setPrediction(d.predicted_reply_rate || null);
      toast.success(
        (d.applied_patterns || []).length > 0
          ? `6 stappen gegenereerd met ${d.applied_patterns.length} bewezen patronen — Auto-Coach loopt op de achtergrond...`
          : '6 stappen gegenereerd — Auto-Coach loopt op de achtergrond...',
      );
      // Poll for auto-coach completion (max ~90s)
      if (d.auto_coach?.status === 'running') {
        let polls = 0;
        const interval = setInterval(async () => {
          polls += 1;
          try {
            const rr = await fetch(`${apiBase}/email/campaigns/${campaign.campaign_id}`, { credentials: 'include' });
            const dd = await rr.json();
            if (dd.auto_coach && dd.auto_coach.status !== 'running') {
              clearInterval(interval);
              setAutoCoach(dd.auto_coach);
              if (dd.sequence_steps) setSteps(dd.sequence_steps);
              if (dd.auto_coach.self_healed) {
                toast.success(`Auto-Coach: ${dd.auto_coach.initial_score} → ${dd.auto_coach.final_score}/100 (zelf-hersteld)`);
              } else if (dd.auto_coach.status === 'done') {
                toast.success(`Auto-Coach klaar: ${dd.auto_coach.final_score}/100`);
              }
            }
          } catch { /* keep polling */ }
          if (polls > 45) {  // 45 × 3s = 135s safety stop
            clearInterval(interval);
          }
        }, 3000);
      }
    } catch (e) {
      toast.error(e.message || 'Generatie mislukt');
    }
    setGenerating(false);
  };

  const previewTargets = async () => {
    try {
      const r = await fetch(`${apiBase}/email/campaigns/${campaign.campaign_id}/preview-targets`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ segment_filter: segmentFilter }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Preview mislukt');
      setPreview(d);
    } catch (e) {
      toast.error(e.message || 'Preview mislukt');
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${apiBase}/email/campaigns/${campaign.campaign_id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product,
          product_description: productDescription,
          target_description: targetDescription,
          sequence_steps: steps,
          segment_filter: segmentFilter,
        }),
      });
      if (!r.ok) throw new Error('Opslaan mislukt');
      toast.success('Campagne opgeslagen');
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e.message || 'Opslaan mislukt');
    }
    setSaving(false);
  };

  const saveAsTemplate = async () => {
    const name = window.prompt('Naam voor nieuwe template:', product || campaign.name || '');
    if (!name || !name.trim()) return;
    try {
      const r = await fetch(`${apiBase}/campaign-templates/from-campaign/${campaign.campaign_id}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim() }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || 'Opslaan als template mislukt');
      }
      toast.success(`Template "${name.trim()}" opgeslagen`);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.message || 'Opslaan als template mislukt');
    }
  };

  const updateStep = (idx, field, val) => {
    setSteps((s) => s.map((st, i) => (i === idx ? { ...st, [field]: val } : st)));
  };

  const reloadPrediction = async (stepsToUse) => {
    const useSteps = stepsToUse || steps;
    if (!useSteps || useSteps.length === 0) return;
    setPredicting(true);
    try {
      const r = await fetch(`${apiBase}/sequence-predict-reply-rate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sequence_steps: useSteps, product }),
      });
      const d = await r.json();
      if (r.ok) setPrediction(d);
    } catch { /* silent */ }
    setPredicting(false);
  };

  const runAutoImprove = async () => {
    if (!product.trim()) {
      toast.error('Vul eerst het product in voordat Auto-Improve kan draaien');
      return;
    }
    setAutoImproving(true);
    try {
      // Persist current state first so the battle starts from the saved product context
      await fetch(`${apiBase}/email/campaigns/${campaign.campaign_id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product, product_description: productDescription, target_description: targetDescription }),
      }).catch(() => {});
      const r = await fetch(`${apiBase}/email/campaigns/${campaign.campaign_id}/auto-improve`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ n_variants: 3 }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Auto-Improve mislukt');
      setVariantBattle({ variants: d.variants, winner_id: d.winner_id, pool_size: d.pool_size, generated_at: d.generated_at });
      setExpandedVariant(d.winner_id || (d.variants?.[0]?.variant_id));
      const winner = (d.variants || []).find((v) => v.variant_id === d.winner_id) || d.variants?.[0];
      const winPct = winner?.prediction?.predicted_reply_rate;
      toast.success(`${d.variants.length} varianten klaar — winnaar: ${winner?.label || 'top'}${winPct != null ? ` (${winPct}%)` : ''}`);
    } catch (e) {
      toast.error(e.message || 'Auto-Improve mislukt');
    }
    setAutoImproving(false);
  };

  const adoptVariant = async (variantId) => {
    setAdoptingVariant(variantId);
    try {
      const r = await fetch(`${apiBase}/email/campaigns/${campaign.campaign_id}/adopt-variant`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ variant_id: variantId }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Adopt mislukt');
      setSteps(d.sequence_steps || []);
      const v = (variantBattle?.variants || []).find((x) => x.variant_id === variantId);
      setAppliedPatterns(v?.patterns_used || []);
      toast.success(`Variant "${v?.label || '?'}" geadopteerd${d.predicted_reply_rate != null ? ` (predicted ${d.predicted_reply_rate}%)` : ''}`);
      reloadPrediction(d.sequence_steps || []);
    } catch (e) {
      toast.error(e.message || 'Adopt mislukt');
    }
    setAdoptingVariant(null);
  };

  const reloadPerStep = async () => {
    if (!campaign?.campaign_id) return;
    setPerStepLoading(true);
    try {
      const r = await fetch(`${apiBase}/email/campaigns/${campaign.campaign_id}/per-step-metrics`, { credentials: 'include' });
      const d = await r.json();
      if (r.ok) setPerStep(d);
    } catch { /* silent */ }
    setPerStepLoading(false);
  };

  const rewriteStep = async (stepNum) => {
    setRewritingStep(stepNum);
    try {
      const r = await fetch(`${apiBase}/email/campaigns/${campaign.campaign_id}/rewrite-step/${stepNum}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: '{}',
      });
      const d = await r.json();
      if (!d.ok) throw new Error(d.error || 'Rewrite mislukt');
      // Update locally so the user immediately sees the new focus
      setSteps((s) => s.map((st) => (Number(st.step) === Number(stepNum) ? { ...st, focus: d.new_focus } : st)));
      toast.success(`Stap ${stepNum} herschreven op basis van live data`);
      reloadPerStep();
    } catch (e) {
      toast.error(e.message || 'Rewrite mislukt');
    }
    setRewritingStep(null);
  };

  const enableStepAB = async () => {
    setEnablingAb(true);
    try {
      const r = await fetch(`${apiBase}/email/campaigns/${campaign.campaign_id}/enable-step-ab`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
      });
      const d = await r.json();
      if (!d.ok) throw new Error(d.error || 'A/B aanzetten mislukt');
      toast.success(`A/B per step actief op ${d.updated_steps} stappen — toekomstige sends worden 50/50 gesplitst`);
      reloadPerStep();
    } catch (e) {
      toast.error(e.message || 'A/B aanzetten mislukt');
    }
    setEnablingAb(false);
  };

  const adoptStepWinners = async () => {
    setAdoptingWinners(true);
    try {
      const r = await fetch(`${apiBase}/email/campaigns/${campaign.campaign_id}/adopt-step-winners`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: '{}',
      });
      const d = await r.json();
      if (!d.ok) throw new Error('Adopt mislukt');
      const n = (d.adopted || []).length;
      toast.success(n > 0 ? `${n} winnaars geadopteerd — A/B tests gesloten` : 'Nog geen stappen klaar voor adoptie');
      reloadPerStep();
    } catch (e) {
      toast.error(e.message || 'Adopt mislukt');
    }
    setAdoptingWinners(false);
  };

  const runCoach = async () => {
    if (!steps.length) {
      toast.error('Genereer eerst de 6 stappen voordat de coach kan beoordelen');
      return;
    }
    setCoaching(true);
    try {
      // Save current state first so coach uses the latest content
      await fetch(`${apiBase}/email/campaigns/${campaign.campaign_id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product, product_description: productDescription,
          target_description: targetDescription,
          sequence_steps: steps, segment_filter: segmentFilter,
        }),
      });
      const r = await fetch(`${apiBase}/email/campaigns/${campaign.campaign_id}/sequence-coach`, {
        method: 'POST', credentials: 'include',
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Coach analyse mislukt');
      setCoachResult(d);
      // Auto-expand the lowest-scoring step so user sees suggestions first
      const lowest = [...(d.steps || [])].sort((a, b) => a.total_score - b.total_score)[0];
      if (lowest) setExpandedCoach({ [lowest.step]: true });
      // Refresh history so the trend reflects this new run
      try {
        const h = await fetch(`${apiBase}/email/campaigns/${campaign.campaign_id}/coach-history`, { credentials: 'include' });
        if (h.ok) {
          const hj = await h.json();
          setCoachHistory(hj.runs || []);
        }
      } catch { /* silent */ }
      if (d.library_promoted) {
        toast.success(`Score ${d.overall_score}/100 — toegevoegd aan Sequence Library`);
      } else {
        toast.success(`Coach klaar — overall score ${d.overall_score}/100`);
      }
    } catch (e) {
      toast.error(e.message || 'Coach mislukt');
    }
    setCoaching(false);
  };

  const coachByStep = (coachResult?.steps || []).reduce((acc, s) => {
    acc[s.step] = s;
    return acc;
  }, {});

  const scoreTheme = (score) => {
    if (score >= 80) return { ring: 'ring-emerald-400', bg: 'bg-emerald-50', border: 'border-emerald-300', text: 'text-emerald-700', bar: 'bg-emerald-500', label: 'Sterk' };
    if (score >= 60) return { ring: 'ring-amber-400', bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-700', bar: 'bg-amber-500', label: 'Kan beter' };
    return { ring: 'ring-rose-400', bg: 'bg-rose-50', border: 'border-rose-300', text: 'text-rose-700', bar: 'bg-rose-500', label: 'Aandacht' };
  };

  const previewEmailForStep = async (stepNumber) => {
    setPreviewingStep(stepNumber);
    setEmailPreview(null);
    try {
      // Save current state first so backend uses latest focus/product
      await fetch(`${apiBase}/email/campaigns/${campaign.campaign_id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product, product_description: productDescription,
          target_description: targetDescription,
          sequence_steps: steps, segment_filter: segmentFilter,
        }),
      });
      const r = await fetch(`${apiBase}/email/campaigns/${campaign.campaign_id}/preview-email`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ step: stepNumber }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Preview generatie mislukt');
      setEmailPreview(d);
    } catch (e) {
      toast.error(e.message || 'Preview mislukt');
    }
    setPreviewingStep(null);
  };

  const toggleArrayFilter = (key, value) => {
    setSegmentFilter((sf) => {
      const cur = Array.isArray(sf[key]) ? [...sf[key]] : [];
      const idx = cur.indexOf(value);
      if (idx >= 0) cur.splice(idx, 1);
      else cur.push(value);
      return { ...sf, [key]: cur };
    });
  };

  const facilityDims = filterConfig?.facility_dimensions || [];
  const countryOptions = ['NL', 'BE', 'DE', 'FR', 'GB', 'IT', 'ES', 'PT', 'IE', 'LU', 'AT', 'CH', 'DK', 'SE', 'NO', 'FI', 'PL', 'CZ', 'HU', 'RO', 'PT'].filter((v, i, a) => a.indexOf(v) === i);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="campaign-product-modal">
      <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[92vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">Product & Sequence — {campaign.name}</h2>
            <p className="text-xs text-zinc-500">Configureer een productspecifieke 6-staps reeks en doelgroepfilter</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-zinc-100 rounded" data-testid="campaign-product-modal-close">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {/* PRODUCT META */}
          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-zinc-700 flex items-center gap-2">
              <Sparkle size={14} className="text-amber-500" /> Product
            </h3>
            <div className="grid grid-cols-1 gap-3">
              <div>
                <Label className="text-xs">Productnaam <span className="text-red-500">*</span></Label>
                <Input
                  value={product}
                  onChange={(e) => setProduct(e.target.value)}
                  placeholder='Bijv. "Betonverf voor outdoor zwembaden"'
                  data-testid="campaign-product-name-input"
                />
              </div>
              <div>
                <Label className="text-xs">Productbeschrijving (gebruikt door AI voor mailteksten)</Label>
                <Textarea
                  value={productDescription}
                  onChange={(e) => setProductDescription(e.target.value)}
                  placeholder="Beschrijf het product, USP's, voordelen, technische specs, prijspositionering..."
                  rows={4}
                  data-testid="campaign-product-description-input"
                />
              </div>
              <div>
                <Label className="text-xs">Doelgroepomschrijving (extra context voor AI)</Label>
                <Textarea
                  value={targetDescription}
                  onChange={(e) => setTargetDescription(e.target.value)}
                  placeholder='Bijv. "Eigenaars van betonnen outdoor zwembaden in NL/DE die binnenkort hun zwembad gaan renoveren of opnieuw schilderen"'
                  rows={2}
                  data-testid="campaign-target-description-input"
                />
              </div>
            </div>
          </section>

          {/* SEGMENT FILTER */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-zinc-700 flex items-center gap-2">
                <Eye size={14} className="text-cyan-600" /> Doelgroep filter
              </h3>
              <Button size="sm" variant="outline" onClick={previewTargets} data-testid="preview-targets-btn">
                <Eye size={12} className="mr-1.5" /> Preview aantal
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Landen</Label>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {countryOptions.map((c) => {
                    const sel = (segmentFilter.country_codes || []).includes(c);
                    return (
                      <button
                        key={c}
                        onClick={() => toggleArrayFilter('country_codes', c)}
                        className={`px-2 py-0.5 text-xs rounded-sm border ${sel ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-50'}`}
                        data-testid={`filter-country-${c}`}
                      >
                        {c}
                      </button>
                    );
                  })}
                </div>
              </div>

              {facilityDims.map((dim) => (
                <div key={dim.key}>
                  <Label className="text-xs">{dim.label || dim.key}</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {(dim.options || dim.values || []).map((opt) => {
                      const v = typeof opt === 'string' ? opt : (opt.value || opt.key);
                      const label = typeof opt === 'string' ? opt : (opt.label || opt.value || opt.key);
                      const arr = Array.isArray(segmentFilter.dimensions?.[dim.key])
                        ? segmentFilter.dimensions[dim.key]
                        : [];
                      const sel = arr.includes(v);
                      return (
                        <button
                          key={v}
                          onClick={() => {
                            setSegmentFilter((sf) => {
                              const dims = { ...(sf.dimensions || {}) };
                              const cur = Array.isArray(dims[dim.key]) ? [...dims[dim.key]] : [];
                              const idx = cur.indexOf(v);
                              if (idx >= 0) cur.splice(idx, 1);
                              else cur.push(v);
                              dims[dim.key] = cur;
                              return { ...sf, dimensions: dims };
                            });
                          }}
                          className={`px-2 py-0.5 text-xs rounded-sm border ${sel ? 'bg-cyan-50 border-cyan-300 text-cyan-700' : 'bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-50'}`}
                          data-testid={`filter-dim-${dim.key}-${v}`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {preview && (
              <div className="bg-zinc-50 rounded-md border border-zinc-200 px-3 py-2 text-xs flex gap-4" data-testid="campaign-target-preview">
                <span><strong>{preview.facilities_match}</strong> facilities</span>
                <span><strong>{preview.contacts_match}</strong> contacten met email</span>
              </div>
            )}
          </section>

          {/* AI GENERATE */}
          <section className="space-y-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <h3 className="text-sm font-semibold text-zinc-700 flex items-center gap-2">
                <MagicWand size={14} className="text-purple-600" /> 6-staps sequence
              </h3>
              <div className="flex items-center gap-2">
                {steps.length > 0 && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={runCoach}
                    disabled={coaching}
                    className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                    data-testid="sequence-coach-btn"
                    title="AI beoordeelt elke stap op uniciteit, lengte, CTA-helderheid en vakjargon"
                  >
                    <ChartLineUp size={12} className="mr-1.5" />
                    {coaching ? 'Coach analyseert...' : coachResult ? 'Coach opnieuw' : 'Coach AI'}
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={runAutoImprove}
                  disabled={autoImproving || !product.trim()}
                  className="border-amber-300 text-amber-700 hover:bg-amber-50"
                  data-testid="auto-improve-btn"
                  title="Genereert 3 sequence-varianten met verschillende bewezen pattern-mixes en voorspelt elk hun reply-rate"
                >
                  <MagicWand size={12} className="mr-1.5" />
                  {autoImproving ? 'Battle loopt...' : 'Auto-Improve'}
                </Button>
                <Button
                  size="sm"
                  onClick={generate}
                  disabled={generating || !product.trim()}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  data-testid="generate-sequence-btn"
                >
                  <Sparkle size={12} className="mr-1.5" />
                  {generating ? 'Genereren...' : steps.length ? 'Opnieuw genereren' : 'AI Genereer 6 stappen'}
                </Button>
              </div>
            </div>

            {coachResult && (
              <div className={`rounded-md border p-3 flex items-start gap-3 ${scoreTheme(coachResult.overall_score).bg} ${scoreTheme(coachResult.overall_score).border}`} data-testid="coach-overall-banner">
                <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center font-data font-bold text-lg ring-2 ring-offset-2 ${scoreTheme(coachResult.overall_score).ring} ${scoreTheme(coachResult.overall_score).text}`} data-testid="coach-overall-score">
                  {coachResult.overall_score}
                </div>
                <div className="flex-1 text-xs">
                  <div className={`font-semibold mb-0.5 flex items-center gap-2 ${scoreTheme(coachResult.overall_score).text}`}>
                    <span>Sequence Coach — {scoreTheme(coachResult.overall_score).label}</span>
                    {coachResult.library_promoted && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-100 border border-emerald-300 text-emerald-700 text-[9px] font-bold uppercase tracking-wide" data-testid="library-promoted-badge">
                        ⭐ In library
                      </span>
                    )}
                  </div>
                  <p className="text-zinc-700 leading-snug mb-2" data-testid="coach-overall-assessment">
                    {coachResult.overall_assessment || 'Klik op een stap om suggesties te bekijken.'}
                  </p>
                  {coachHistory.length > 1 && (
                    <div className="flex items-end gap-1 mt-1" data-testid="coach-history-sparkline" title={`${coachHistory.length} coach-runs — laatste rechts`}>
                      <span className="text-[10px] text-zinc-500 mr-1">Trend:</span>
                      {coachHistory.slice(-12).map((h, i, arr) => {
                        const theme = scoreTheme(h.overall_score);
                        const isLast = i === arr.length - 1;
                        return (
                          <div key={h.history_id || i} className="flex flex-col items-center" style={{ width: 14 }}>
                            <div
                              className={`w-2.5 ${theme.bar} ${isLast ? 'ring-1 ring-zinc-900 ring-offset-1' : 'opacity-80'} rounded-sm`}
                              style={{ height: `${Math.max(2, Math.round((h.overall_score / 100) * 28))}px` }}
                              title={`${h.overall_score}/100 — ${new Date(h.run_at).toLocaleDateString('nl-NL')}`}
                            />
                            <span className={`text-[8px] font-data mt-0.5 ${isLast ? 'text-zinc-900 font-bold' : 'text-zinc-400'}`}>{h.overall_score}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {prediction && prediction.predicted_reply_rate !== null && (
              <div className="rounded-md border-2 border-emerald-300 bg-gradient-to-r from-emerald-50 to-blue-50 p-3 flex items-center gap-4" data-testid="reply-rate-prediction">
                <div className="flex-shrink-0">
                  <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-0.5">📈 Voorspelde reply-rate</div>
                  <div className="flex items-baseline gap-1">
                    <span className="font-data font-bold text-2xl text-emerald-700" data-testid="prediction-mid">
                      {prediction.predicted_reply_rate}%
                    </span>
                    <span className="text-[10px] text-zinc-500">
                      ({prediction.low_pct}–{prediction.high_pct}%)
                    </span>
                  </div>
                </div>
                <div className="flex-1 min-w-0 text-[11px] text-zinc-700">
                  <p>
                    Baseline library: <strong>{prediction.baseline_reply_rate}%</strong>
                    {' · '}sample: <strong>{prediction.training_step_count} steps</strong>
                    {' · '}vertrouwen:{' '}
                    <span className={
                      prediction.sample_size === 'high' ? 'text-emerald-700 font-semibold' :
                      prediction.sample_size === 'medium' ? 'text-amber-700 font-semibold' :
                      'text-rose-700 font-semibold'
                    }>{prediction.sample_size === 'high' ? 'hoog' : prediction.sample_size === 'medium' ? 'medium' : 'laag'}</span>
                    {predicting && <span className="ml-2 text-zinc-400 italic">(berekenen...)</span>}
                  </p>
                  {(prediction.applied_signals || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {prediction.applied_signals.slice(0, 3).map((s, i) => (
                        <span key={i} className={`px-1.5 py-0.5 rounded-full text-[9px] border ${s.total_delta >= 0 ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-rose-50 border-rose-200 text-rose-800'}`}>
                          {s.total_delta >= 0 ? '+' : ''}{s.total_delta.toFixed(1)}% · {s.bucket}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
            {prediction && prediction.predicted_reply_rate === null && prediction.note && (
              <div className="rounded-md border border-zinc-300 bg-zinc-50 p-3 text-[11px] text-zinc-600 italic" data-testid="reply-rate-no-data">
                📈 {prediction.note}
              </div>
            )}

            {autoCoach && (
              <div className={`rounded-md border-2 p-3 ${
                autoCoach.status === 'running' ? 'border-indigo-300 bg-indigo-50' :
                autoCoach.self_healed ? 'border-amber-300 bg-amber-50' :
                autoCoach.final_score >= 80 ? 'border-emerald-300 bg-emerald-50' :
                autoCoach.status === 'error' || autoCoach.status === 'skipped' ? 'border-zinc-300 bg-zinc-50' :
                'border-zinc-300 bg-zinc-50'
              }`} data-testid="auto-coach-banner">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-base">
                    {autoCoach.status === 'running' ? (
                      <span className="inline-block w-3 h-3 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                    ) : autoCoach.self_healed ? '🩹' : autoCoach.final_score >= 80 ? '✅' : autoCoach.status === 'error' ? '⚠️' : '🤖'}
                  </span>
                  <h4 className="text-xs font-semibold text-zinc-900">
                    {autoCoach.status === 'running'
                      ? 'Auto-Coach analyseert de sequence...'
                      : autoCoach.self_healed
                      ? 'Auto-Coach Self-Healing toegepast'
                      : autoCoach.status === 'skipped'
                      ? 'Auto-Coach overgeslagen'
                      : autoCoach.status === 'error'
                      ? 'Auto-Coach kon niet draaien'
                      : autoCoach.final_score >= 80
                      ? `Auto-Coach: direct ${autoCoach.final_score}/100 — geen retry nodig`
                      : `Auto-Coach: ${autoCoach.final_score}/100 — handmatig kan beter`}
                  </h4>
                </div>
                {autoCoach.status === 'running' && (
                  <p className="text-[11px] text-indigo-700">
                    Coach beoordeelt + zelf-heelt bij score &lt;80. Dit kan tot 60s duren.
                  </p>
                )}
                {autoCoach.status === 'done' && autoCoach.self_healed && autoCoach.weak_step && (
                  <p className="text-[11px] text-zinc-700 mb-2">
                    Initial: <strong>{autoCoach.initial_score}/100</strong> → na retry: <strong className="text-emerald-700">{autoCoach.final_score}/100</strong>
                    {' · '}zwakste step was <strong>{autoCoach.weak_step.name}</strong> (score {autoCoach.weak_step.score})
                  </p>
                )}
                {autoCoach.status === 'done' && !autoCoach.self_healed && (
                  <p className="text-[11px] text-zinc-600">
                    Score: <strong>{autoCoach.final_score}/100</strong>
                    {autoCoach.final_score < 80 && ' — overweeg "Opnieuw genereren" of pas handmatig de focus-teksten aan.'}
                  </p>
                )}
                {(autoCoach.status === 'error' || autoCoach.status === 'skipped') && autoCoach.error && (
                  <p className="text-[11px] text-zinc-500 italic">{autoCoach.error}</p>
                )}
              </div>
            )}

            {variantBattle && (variantBattle.variants || []).length > 0 && (
              <div className="rounded-md border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50 p-4 space-y-3" data-testid="variant-battle-panel">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wide text-amber-900 flex items-center gap-1.5">
                      <span>🥊</span> Variant Battle — {variantBattle.variants.length} kandidaten
                    </h4>
                    <p className="text-[11px] text-amber-800/80 mt-0.5">
                      Elke variant is gegenereerd met een andere pattern-mix uit de library en is afzonderlijk voorspeld.
                      Adopt de winnaar of bekijk de alternatieven.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setVariantBattle(null)}
                    className="text-amber-700 hover:text-amber-900"
                    data-testid="variant-battle-close"
                    title="Battle sluiten"
                  >
                    <X size={14} />
                  </button>
                </div>
                <div className="space-y-2">
                  {variantBattle.variants.map((v, idx) => {
                    const mid = v.prediction?.predicted_reply_rate;
                    const lo = v.prediction?.low_pct;
                    const hi = v.prediction?.high_pct;
                    const isWinner = v.variant_id === variantBattle.winner_id;
                    const isExpanded = expandedVariant === v.variant_id;
                    return (
                      <div
                        key={v.variant_id}
                        className={`rounded-md border bg-white ${isWinner ? 'border-emerald-400 ring-1 ring-emerald-200' : 'border-zinc-200'}`}
                        data-testid={`variant-${idx}`}
                      >
                        <div className="flex items-center gap-3 px-3 py-2">
                          <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-data font-bold text-sm ${isWinner ? 'bg-emerald-500 text-white' : 'bg-zinc-100 text-zinc-700'}`}>
                            {isWinner ? '🏆' : `#${idx + 1}`}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold text-zinc-900">{v.label}</span>
                              {isWinner && (
                                <span className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[9px] font-bold uppercase tracking-wide" data-testid={`variant-${idx}-winner-badge`}>
                                  Winnaar
                                </span>
                              )}
                            </div>
                            {v.description && (
                              <p className="text-[10px] text-zinc-500 truncate">{v.description}</p>
                            )}
                            <div className="flex items-baseline gap-2 mt-1">
                              <span className={`font-data font-bold text-base ${isWinner ? 'text-emerald-700' : 'text-zinc-800'}`} data-testid={`variant-${idx}-predicted`}>
                                {mid != null ? `${mid}%` : 'n.v.t.'}
                              </span>
                              {(lo != null && hi != null) && (
                                <span className="text-[10px] text-zinc-500">({lo}–{hi}%)</span>
                              )}
                              <span className="text-[10px] text-zinc-400">· {(v.patterns_used || []).length} patronen</span>
                            </div>
                          </div>
                          <div className="flex flex-col gap-1 flex-shrink-0">
                            <Button
                              size="sm"
                              onClick={() => adoptVariant(v.variant_id)}
                              disabled={adoptingVariant === v.variant_id}
                              className={`h-7 text-[11px] ${isWinner ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-zinc-900 hover:bg-zinc-800 text-white'}`}
                              data-testid={`variant-${idx}-adopt-btn`}
                            >
                              {adoptingVariant === v.variant_id ? 'Adopteren...' : 'Adopt'}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setExpandedVariant(isExpanded ? null : v.variant_id)}
                              className="h-6 text-[10px]"
                              data-testid={`variant-${idx}-toggle`}
                            >
                              {isExpanded ? 'Verbergen' : 'Bekijk'}
                            </Button>
                          </div>
                        </div>
                        {isExpanded && (
                          <div className="border-t border-zinc-100 px-3 py-2 bg-zinc-50/60 space-y-2">
                            {(v.patterns_used || []).length > 0 && (
                              <div>
                                <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-1">Pattern-mix</div>
                                <div className="flex flex-wrap gap-1">
                                  {v.patterns_used.map((p, pi) => (
                                    <span key={pi} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-violet-50 border border-violet-200 text-[9px] text-violet-800" data-testid={`variant-${idx}-pattern-${pi}`}>
                                      <span className="font-data font-bold text-emerald-600">+{p.lift_pct}%</span>
                                      <span className="font-semibold">{p.feature_label || p.feature}</span>
                                      <span className="text-zinc-500">· {p.bucket}</span>
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                            <div>
                              <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-1">Sequence preview</div>
                              <div className="space-y-1.5">
                                {(v.sequence_steps || []).map((st, si) => (
                                  <div key={si} className="text-[11px] leading-snug bg-white border border-zinc-200 rounded p-2" data-testid={`variant-${idx}-step-${si + 1}`}>
                                    <span className="font-semibold text-zinc-700">Stap {st.step || si + 1} — {st.name}</span>
                                    <span className="text-zinc-500"> · {st.goal}</span>
                                    <p className="text-zinc-600 mt-0.5 line-clamp-2">{st.focus}</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {(retroLoading || retro) && (
              <div className="rounded-md border-2 border-sky-300 bg-gradient-to-br from-sky-50 to-cyan-50 p-3 space-y-2" data-testid="variant-retrospective-panel">
                <div className="flex items-center gap-2">
                  <span className="text-lg">🔁</span>
                  <h4 className="text-xs font-bold uppercase tracking-wide text-sky-900">
                    Variant Battle Retrospective
                  </h4>
                  {retro?.eligible === false && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-zinc-200 text-zinc-600 uppercase tracking-wide">
                      {retro.reason === 'insufficient_sends' ? `${retro.sent || 0}/${retro.min_sent} sends` : 'wacht op data'}
                    </span>
                  )}
                </div>
                {retroLoading && <p className="text-[11px] text-sky-700 italic">Retrospective berekenen...</p>}
                {!retroLoading && retro?.eligible === false && retro.reason === 'insufficient_sends' && (
                  <p className="text-[11px] text-zinc-700">
                    Nog te weinig verzonden e-mails om predictie vs werkelijkheid te vergelijken.
                    Minimum: <strong>{retro.min_sent}</strong> verzonden mails.
                  </p>
                )}
                {!retroLoading && retro?.eligible === false && retro.reason !== 'insufficient_sends' && (
                  <p className="text-[11px] text-zinc-600 italic">
                    Geen volledige battle + adoption gevonden voor deze campagne.
                  </p>
                )}
                {!retroLoading && retro?.eligible && (
                  <>
                    <div className="grid grid-cols-3 gap-3 bg-white border border-sky-200 rounded-md p-2.5">
                      <div className="text-center">
                        <div className="text-[10px] uppercase text-zinc-500 tracking-wide">📨 Sends</div>
                        <div className="font-data font-bold text-zinc-900 text-base" data-testid="retro-sent">{retro.sent}</div>
                      </div>
                      <div className="text-center border-x border-zinc-100">
                        <div className="text-[10px] uppercase text-zinc-500 tracking-wide">📊 Echte reply</div>
                        <div className="font-data font-bold text-emerald-700 text-base" data-testid="retro-actual">{retro.actual_reply_rate}%</div>
                      </div>
                      <div className="text-center">
                        <div className="text-[10px] uppercase text-zinc-500 tracking-wide">Δ vs predicted</div>
                        {retro.adopted_error == null ? (
                          <div className="font-data font-bold text-zinc-400 text-base">–</div>
                        ) : (
                          <div className={`font-data font-bold text-base ${Math.abs(retro.adopted_error) < 2 ? 'text-emerald-700' : Math.abs(retro.adopted_error) < 5 ? 'text-amber-700' : 'text-rose-700'}`} data-testid="retro-error">
                            {retro.adopted_error > 0 ? '+' : ''}{retro.adopted_error}%
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="space-y-1">
                      {(retro.variants || []).map((v, idx) => (
                        <div key={v.variant_id} className={`flex items-center gap-2 px-2.5 py-1.5 rounded text-[11px] ${v.is_adopted ? 'bg-emerald-100/60 border border-emerald-300' : 'bg-white/60 border border-zinc-200'}`} data-testid={`retro-variant-${idx}`}>
                          <span className="font-semibold flex-shrink-0">{v.is_adopted ? '✅' : v.is_winner ? '🥈' : '#'}</span>
                          <span className="flex-1 truncate text-zinc-800">{v.label}</span>
                          <span className="font-data text-zinc-700">predicted <strong>{v.predicted_reply_rate ?? '–'}%</strong></span>
                          {v.is_adopted && (
                            <span className="font-data text-emerald-800">→ actual <strong>{v.actual_reply_rate}%</strong></span>
                          )}
                        </div>
                      ))}
                    </div>
                    {retro.adopted_error != null && (
                      <p className="text-[10px] text-zinc-600 italic">
                        {Math.abs(retro.adopted_error) < 2
                          ? '🎯 Predictor zat dichtbij. Vertrouwen in toekomstige battles toegenomen.'
                          : retro.adopted_error > 0
                          ? `📈 Echte performance overtrof voorspelling met ${retro.adopted_error}% — predictor onderschat dit type sequence.`
                          : `📉 Echte performance bleef ${Math.abs(retro.adopted_error)}% onder voorspelling — predictor overschat dit type sequence.`}
                      </p>
                    )}
                  </>
                )}
              </div>
            )}

            {(perStep || perStepLoading) && (
              <div className="rounded-md border-2 border-violet-300 bg-gradient-to-br from-violet-50 to-fuchsia-50 p-3 space-y-2" data-testid="per-step-performance-panel">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">⚙️</span>
                    <h4 className="text-xs font-bold uppercase tracking-wide text-violet-900">Per-Step Performance & A/B</h4>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={enableStepAB}
                      disabled={enablingAb}
                      className="h-7 text-[11px] border-violet-300 text-violet-700 hover:bg-violet-100"
                      data-testid="enable-step-ab-btn"
                      title="Genereert een B-variant voor elke step → 50/50 split bij send-time"
                    >
                      {enablingAb ? 'A/B aanzetten...' : '🔀 A/B per step aanzetten'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={adoptStepWinners}
                      disabled={adoptingWinners}
                      className="h-7 text-[11px] border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                      data-testid="adopt-step-winners-btn"
                      title="Lock A/B winnaars in voor elke stap met genoeg sample"
                    >
                      {adoptingWinners ? 'Adopteren...' : '🏆 Adopt winnaars'}
                    </Button>
                  </div>
                </div>
                {perStepLoading && <p className="text-[11px] text-violet-700 italic">Live engagement laden...</p>}
                {!perStepLoading && perStep && (perStep.steps || []).length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-[11px] border-collapse" data-testid="per-step-table">
                      <thead>
                        <tr className="text-zinc-500 uppercase tracking-wider text-[9px]">
                          <th className="text-left px-1 py-1">Stap</th>
                          <th className="text-right px-1 py-1">Sends</th>
                          <th className="text-right px-1 py-1">Replies</th>
                          <th className="text-right px-1 py-1">Actual</th>
                          <th className="text-right px-1 py-1">Predicted</th>
                          <th className="text-right px-1 py-1">Δ</th>
                          <th className="text-center px-1 py-1">A/B</th>
                          <th className="text-right px-1 py-1"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {perStep.steps.map((s) => {
                          const verdictBadge = {
                            weak: { bg: 'bg-rose-100', text: 'text-rose-700', label: '⚠ Weak' },
                            strong: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: '🚀 Strong' },
                            on_track: { bg: 'bg-zinc-100', text: 'text-zinc-700', label: 'OK' },
                            no_data: { bg: 'bg-zinc-50', text: 'text-zinc-400', label: '—' },
                          }[s.verdict] || { bg: 'bg-zinc-50', text: 'text-zinc-400', label: '—' };
                          const stepVariants = (perStep.per_step_variants || []).find((v) => v.step === s.step);
                          return (
                            <tr key={s.step} className="border-t border-violet-200/60 bg-white/60" data-testid={`per-step-row-${s.step}`}>
                              <td className="px-1 py-1.5">
                                <div className="font-semibold text-zinc-900">#{s.step}</div>
                                <div className="text-[9px] text-zinc-500 truncate max-w-[100px]">{s.name}</div>
                              </td>
                              <td className="px-1 py-1.5 text-right font-data">{s.sent}</td>
                              <td className="px-1 py-1.5 text-right font-data">{s.replies}</td>
                              <td className="px-1 py-1.5 text-right font-data font-semibold text-zinc-900">{s.reply_rate}%</td>
                              <td className="px-1 py-1.5 text-right font-data text-zinc-500">{s.predicted_reply_rate ?? '–'}{s.predicted_reply_rate != null ? '%' : ''}</td>
                              <td className="px-1 py-1.5 text-right">
                                {s.delta == null ? <span className="text-zinc-400">–</span> : (
                                  <span className={`font-data font-bold ${s.delta >= 0 ? 'text-emerald-700' : 'text-rose-700'}`} data-testid={`per-step-delta-${s.step}`}>
                                    {s.delta > 0 ? '+' : ''}{s.delta}%
                                  </span>
                                )}
                                <div className={`text-[9px] px-1 py-0.5 rounded inline-block mt-0.5 ${verdictBadge.bg} ${verdictBadge.text}`}>{verdictBadge.label}</div>
                              </td>
                              <td className="px-1 py-1.5 text-center text-[10px]">
                                {stepVariants ? (
                                  stepVariants.ready && stepVariants.winner_id ? (
                                    <span className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-bold" title={`Winnaar: ${stepVariants.variants.find((v) => v.variant_id === stepVariants.winner_id)?.label}`}>
                                      🏆 {stepVariants.variants.find((v) => v.variant_id === stepVariants.winner_id)?.label}
                                    </span>
                                  ) : s.has_ab_variants ? (
                                    <span className="px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700" title="A/B actief, nog niet genoeg sample">
                                      🔀 actief
                                    </span>
                                  ) : <span className="text-zinc-300">–</span>
                                ) : s.has_ab_variants ? (
                                  <span className="px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700">🔀</span>
                                ) : <span className="text-zinc-300">–</span>}
                              </td>
                              <td className="px-1 py-1.5 text-right">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => rewriteStep(s.step)}
                                  disabled={rewritingStep === s.step}
                                  className="h-6 text-[10px] px-1.5 border-rose-300 text-rose-700 hover:bg-rose-50"
                                  data-testid={`rewrite-step-${s.step}-btn`}
                                  title={s.verdict === 'weak' ? `Stap presteert ${Math.abs(s.delta)}% onder voorspelling — herschrijf focus` : 'Herschrijf focus op basis van live data'}
                                >
                                  {rewritingStep === s.step ? '...' : '🛠 Rewrite'}
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {(perStep.weak_steps || []).length > 0 && (
                      <p className="text-[10px] text-rose-700 mt-1.5 italic" data-testid="per-step-weak-summary">
                        {perStep.weak_steps.length} {perStep.weak_steps.length === 1 ? 'stap presteert' : 'stappen presteren'} ≥5% onder voorspelling. Auto-optimizer schrijft elke 60 min automatisch een nieuwe focus — of klik op 🛠 Rewrite voor onmiddellijke actie.
                      </p>
                    )}
                  </div>
                )}
                {!perStepLoading && perStep && (perStep.steps || []).length === 0 && (
                  <p className="text-[11px] text-zinc-500 italic">Nog geen sequence — genereer eerst de 6 stappen.</p>
                )}
              </div>
            )}

            {appliedPatterns.length > 0 && (
              <div className="rounded-md border-2 border-violet-300 bg-violet-50 p-3" data-testid="applied-patterns-banner">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-lg">🧬</span>
                  <h4 className="text-xs font-semibold text-violet-900">
                    {appliedPatterns.length} bewezen patronen toegepast in deze sequence
                  </h4>
                </div>
                <p className="text-[11px] text-violet-700 mb-2">
                  Uit cross-profile Library Insights — elke step is gegenereerd met deze structurele richtlijnen ingebakken.
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {appliedPatterns.map((p, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white border border-violet-200 text-[10px] text-violet-800" data-testid={`applied-pattern-${i}`}>
                      <span className="text-emerald-600 font-data font-bold">+{p.lift_pct}%</span>
                      <span className="font-semibold">{p.feature_label}</span>
                      <span className="text-zinc-500">· {p.bucket}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {steps.length === 0 && (
              <p className="text-xs text-zinc-500 italic">
                Klik op "AI Genereer" om 6 stappen te maken die specifiek over jouw product gaan.
                Daarna kun je elke stap nog handmatig aanpassen.
              </p>
            )}

            <div className="space-y-3">
              {steps.map((st, i) => (
                <div key={i} className="border border-zinc-200 rounded-md p-3 bg-white" data-testid={`sequence-step-${st.step || i + 1}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="rounded-sm px-2 py-0.5 bg-zinc-100 text-zinc-700 border border-zinc-200">Stap {st.step || i + 1}</Badge>
                    <Input
                      value={st.name || ''}
                      onChange={(e) => updateStep(i, 'name', e.target.value)}
                      className="h-7 text-xs"
                      placeholder="Stap naam"
                      data-testid={`step-${i + 1}-name`}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => previewEmailForStep(st.step || i + 1)}
                      disabled={previewingStep === (st.step || i + 1)}
                      className="h-7 px-2 text-[10px]"
                      data-testid={`step-${i + 1}-preview-btn`}
                    >
                      <Eye size={11} className="mr-1" />
                      {previewingStep === (st.step || i + 1) ? 'AI bezig...' : 'Preview email'}
                    </Button>
                  </div>
                  <Label className="text-[10px] text-zinc-500">Doel</Label>
                  <Input
                    value={st.goal || ''}
                    onChange={(e) => updateStep(i, 'goal', e.target.value)}
                    className="h-7 text-xs mb-2"
                    placeholder="Wat moet deze mail bereiken?"
                    data-testid={`step-${i + 1}-goal`}
                  />
                  <Label className="text-[10px] text-zinc-500">Focus / instructie voor AI</Label>
                  <Textarea
                    value={st.focus || ''}
                    onChange={(e) => updateStep(i, 'focus', e.target.value)}
                    rows={3}
                    className="text-xs"
                    placeholder="Specifieke instructie voor de inhoud van deze mail"
                    data-testid={`step-${i + 1}-focus`}
                  />
                  {coachByStep[st.step || i + 1] && (() => {
                    const sc = coachByStep[st.step || i + 1];
                    const theme = scoreTheme(sc.total_score);
                    const isOpen = !!expandedCoach[st.step || i + 1];
                    return (
                      <div className={`mt-3 rounded-md border ${theme.border} ${theme.bg}`} data-testid={`step-${i + 1}-coach-card`}>
                        <button
                          type="button"
                          onClick={() => setExpandedCoach((m) => ({ ...m, [st.step || i + 1]: !m[st.step || i + 1] }))}
                          className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left"
                          data-testid={`step-${i + 1}-coach-toggle`}
                        >
                          <div className="flex items-center gap-2">
                            <span className={`font-data font-bold text-base ${theme.text}`} data-testid={`step-${i + 1}-coach-score`}>{sc.total_score}</span>
                            <span className={`text-[10px] uppercase font-semibold tracking-wide ${theme.text}`}>{theme.label}</span>
                            <span className="text-[10px] text-zinc-500">· {sc.suggestions.length} suggesties</span>
                          </div>
                          {isOpen ? <CaretDown size={12} className={theme.text} /> : <CaretRight size={12} className={theme.text} />}
                        </button>
                        {isOpen && (
                          <div className="px-3 pb-3 space-y-2">
                            <div className="grid grid-cols-2 gap-2 text-[10px]">
                              {Object.entries(sc.scores).map(([k, v]) => {
                                const labels = { uniqueness: 'Uniciteit', length: 'Lengte', cta_clarity: 'CTA-helderheid', jargon: 'Vakjargon' };
                                return (
                                  <div key={k} className="flex items-center gap-1.5" data-testid={`step-${i + 1}-coach-${k}`}>
                                    <span className="text-zinc-500 w-24 flex-shrink-0">{labels[k] || k}</span>
                                    <div className="flex-1 h-1.5 bg-zinc-200 rounded-full overflow-hidden">
                                      <div className={`h-full ${scoreTheme(v).bar}`} style={{ width: `${v}%` }} />
                                    </div>
                                    <span className={`font-data w-7 text-right ${scoreTheme(v).text}`}>{v}</span>
                                  </div>
                                );
                              })}
                            </div>
                            {sc.suggestions.length > 0 && (
                              <div className="pt-2 border-t border-current border-opacity-20">
                                <div className="text-[10px] font-semibold text-zinc-600 mb-1">Concrete suggesties</div>
                                <ul className="space-y-1 list-none">
                                  {sc.suggestions.map((sg, j) => (
                                    <li key={j} className="text-[11px] text-zinc-700 flex gap-1.5" data-testid={`step-${i + 1}-coach-suggestion-${j}`}>
                                      <span className={theme.text}>→</span>
                                      <span>{sg}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              ))}
            </div>
          </section>
        </div>

        <div className="px-6 py-3 border-t border-zinc-200 bg-zinc-50 flex items-center justify-between">
          <span className="text-xs text-zinc-500">
            Tip: zonder ingevulde sequence valt de campagne terug op de profiel-default stappen.
          </span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={saveAsTemplate} disabled={saving} data-testid="campaign-product-save-template-btn">
              <BookmarkSimple size={12} className="mr-1.5" /> Opslaan als template
            </Button>
            <Button variant="outline" onClick={onClose} data-testid="campaign-product-cancel-btn">Annuleren</Button>
            <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="campaign-product-save-btn">
              <FloppyDisk size={12} className="mr-1.5" /> {saving ? 'Opslaan...' : 'Opslaan'}
            </Button>
          </div>
        </div>
      </div>

      {/* EMAIL PREVIEW OVERLAY */}
      {emailPreview && (
        <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="email-preview-overlay">
          <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Eye size={16} className="text-cyan-600" />
                <div>
                  <div className="text-sm font-semibold">Email preview — Stap {emailPreview.step}</div>
                  <div className="text-[11px] text-zinc-500">
                    {emailPreview.sample_contact?.name || '—'}
                    {emailPreview.sample_contact?.email ? ` (${emailPreview.sample_contact.email})` : ''}
                    {emailPreview.sample_facility?.name ? ` · ${emailPreview.sample_facility.name}` : ''}
                  </div>
                </div>
              </div>
              <button onClick={() => setEmailPreview(null)} className="p-1.5 hover:bg-zinc-100 rounded" data-testid="email-preview-close">
                <X size={16} />
              </button>
            </div>
            <div className="px-5 py-3 border-b border-zinc-100">
              <div className="text-[10px] text-zinc-400 uppercase">Onderwerp</div>
              <div className="text-sm font-medium">{emailPreview.subject || '—'}</div>
              {emailPreview.preview_text && (
                <div className="text-[11px] text-zinc-500 italic mt-0.5">Preview: {emailPreview.preview_text}</div>
              )}
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {emailPreview.html_body ? (
                <div className="border border-zinc-200 rounded p-4 bg-white" dangerouslySetInnerHTML={{ __html: emailPreview.html_body }} />
              ) : (
                <pre className="text-xs whitespace-pre-wrap bg-zinc-50 p-3 rounded">{emailPreview.text_body || '—'}</pre>
              )}
            </div>
            <div className="px-5 py-3 border-t border-zinc-200 bg-zinc-50 flex justify-between items-center">
              <span className="text-[11px] text-zinc-500">
                Steekproef voor 1 contact uit de doelgroep — daadwerkelijke emails worden bij verzending opnieuw gegenereerd per contact.
              </span>
              <Button size="sm" variant="outline" onClick={() => previewEmailForStep(emailPreview.step)} data-testid="email-preview-regenerate">
                <MagicWand size={11} className="mr-1.5" /> Opnieuw genereren
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
